import { IconButton } from '@/styles/Buttons';
import { FlexColumn, FlexContainer } from '@/styles/Containers';
import { Oval } from '@/styles/Shapes';
import { CustomizableHeading, GreyText, Letter } from '@/styles/Text';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import deleteIcon from '../../assets/icons/delete.svg';
import Tooltip from '../course/Tooltip';
import useSize from '@/utils/useSize';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '@/config';
import { gradeValues } from './CalculatorTypes';

export interface CalculatorCourseProps {
  courseCode: string;
  courseName: string;
  coursePoint: number;
  grade: number;
  onSelectedGrade: (courseCode: string, selectedValue: number) => void;
  onDelete: (coursecode: string) => void;
  onGradeChange: (newGrade: number) => void;
}

const CalculatorCourseContainer = styled.div`
  display: flex;
  position: relative;
  overflow: hidden;
  align-items: center;
  margin: 0.1rem;
  width: 100%;
  border-radius: 0.6rem;
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.black};
  padding: 0.5rem 1% 0.5rem;
  @media ${({ theme }) => theme.device.mobileL} {
    padding: 1.5rem;
    background-color: ${({ theme }) => theme.color.lightlightBlue};
    border-bottom: none;
    border-left: none;
    border-right: none;
    border-radius: 0;
    width: 90%;
  }
`;

export const Circle = styled.div`
  border-radius: 50%;
  height: 2rem;
  width: 2rem;
  size: 2rem;
  border: 1px solid ${({ theme }) => theme.color.darkBlue};
  justify-content: center;
  background-color: ${({ color }) => color};
  :hover {
    background-color: ${({ theme }) => theme.color.darkBlue};
    cursor: pointer;
    transform: scale(1.1);
  }
`;

const CalculatorCourse = ({ courseCode, courseName, coursePoint, grade, onSelectedGrade, onDelete }: CalculatorCourseProps) => {
  const [isDelete, setIsDelete] = useState(false);
  const { isMobile, size } = useSize();
  const [isSelected, setIsSelected] = useState(false);

  let displayCourseName = courseName;
  if (courseName.length > 15) {
    const firstPart = courseName.split(' ')[0];
    displayCourseName = `${firstPart}(...)`;
    if (isMobile && displayCourseName.length > 18) {
      const firstPart = courseName.substring(0, 9);
      displayCourseName = `${firstPart}(...)`;
    } else if (displayCourseName.length > 16) {
      const firstPart = courseName.substring(0, 12);
      displayCourseName = `${firstPart}(...)`;
    }
  }

  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedValue, setSelectedValue] = useState(grade);
  const grades: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'Bestått'];

  const useGradeMutation = () => {
    return useMutation(async ({ course_code, grade, semester }: { course_code: string; grade: number; semester: string }) => {
      const response = await axios.put(`${API_URL}/calculator/update_grades/`, {
        course_code,
        grade,
        semester,
      });
      return response.data;
    });
  };

  const selectGrade = async (grade: string) => {
    setSelectedGrade(grade);
    setIsSelected(true);
    const newValue = gradeValues[grade];
    setSelectedValue(newValue);
    onSelectedGrade(courseCode, newValue);

    try {
      await mutation.mutateAsync({
        course_code: courseCode,
        grade: newValue,
        semester: '0',
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await axios.put(`${API_URL}/calculator/update_grades/`, {
          course_code: courseCode,
          grade: newValue,
          semester: '0',
        });
      }
    }
  };

  const mutation = useGradeMutation();

  useEffect(() => {
    const selectedLetter = Object.keys(gradeValues).find((key) => gradeValues[key] === grade);
    if (selectedLetter && selectedLetter != null) {
      setSelectedGrade(selectedLetter);
    }
  }, [grade]);

  const handleDelete = () => {
    onDelete(courseCode);
    setIsDelete(true);
  };

  return (
    <>
      {!isDelete && !isMobile && (
        <CalculatorCourseContainer>
          <FlexContainer width='100%' justifyContent='space-between' alignItems='center' margin='0 2.4rem'>
            <FlexColumn width='7rem'>
              <CustomizableHeading fontSize='1rem' mobileFontSize='1rem' headingLevel='h3'>
                {courseCode}
              </CustomizableHeading>
              <GreyText>{coursePoint}</GreyText>
            </FlexColumn>
            <FlexContainer width='7rem' margin='0 4rem'>
              <Tooltip text={courseName}>
                <CustomizableHeading fontSize='1rem' mobileFontSize='1rem' headingLevel='h4' textAlign='right'>
                  {displayCourseName}
                </CustomizableHeading>
              </Tooltip>
            </FlexContainer>
            <FlexContainer gap='1rem' justifyContent='flex-end'>
              {grades.slice(0, 6).map((grade) => (
                <Circle key={grade} onClick={() => selectGrade(grade)} style={{ backgroundColor: selectedGrade === grade ? '#1E3859' : 'white' }}>
                  <Letter style={{ color: selectedGrade === grade ? 'white' : 'black' }}>{grade}</Letter>
                </Circle>
              ))}
              {grades.slice(6, 7).map((grade) => (
                <Oval
                  key={grade}
                  style={{
                    backgroundColor: selectedGrade === grade ? '#1e3859' : 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onClick={() => selectGrade(grade)}
                >
                  <h3 style={{ color: selectedGrade === grade ? 'white' : 'black' }}>Bestått</h3>
                </Oval>
              ))}
              <IconButton margin='0' onClick={handleDelete}>
                <img src={deleteIcon} alt='trash bin' />
              </IconButton>
            </FlexContainer>
          </FlexContainer>
        </CalculatorCourseContainer>
      )}
      {!isDelete && isMobile && (
        <CalculatorCourseContainer>
          <FlexContainer flexDirection='column' width='100%' margin='0 1rem 0 -1.4rem'>
            <FlexContainer>
              <FlexContainer width='17rem'>
                <FlexColumn>
                  <CustomizableHeading fontSize='0.9rem' mobileFontSize='0.9rem' headingLevel='h2' margin='0 1rem 0 2rem'>
                    {courseCode}
                  </CustomizableHeading>
                  <GreyText>{coursePoint}</GreyText>
                </FlexColumn>
                <div style={{ width: 'rem', marginLeft: '4rem' }}>
                  <Tooltip text={courseName}>
                    <CustomizableHeading fontSize='0.9rem' mobileFontSize='0.9rem' headingLevel='h4' textAlign='right' margin='0rem -0.5rem'>
                      {displayCourseName}
                    </CustomizableHeading>
                  </Tooltip>
                </div>
              </FlexContainer>
              <IconButton onClick={handleDelete} margin='0rem 1rem' position='static'>
                <img src={deleteIcon} alt='trash bin' />
              </IconButton>
            </FlexContainer>
            <FlexContainer justifyContent='left' gap='0.1rem'>
              {grades.slice(0, 6).map((grade) => (
                <Circle key={grade} onClick={() => selectGrade(grade)} style={{ backgroundColor: selectedGrade === grade ? '#1E3859' : 'white' }}>
                  <Letter style={{ color: selectedGrade === grade ? 'white' : 'black' }}>{grade}</Letter>
                </Circle>
              ))}
              {grades.slice(6, 7).map((grade) => (
                <Oval
                  key={grade}
                  style={{
                    backgroundColor: selectedGrade === grade ? '#1e3859' : 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onClick={() => selectGrade(grade)}
                >
                  <h4 style={{ color: selectedGrade === grade ? 'white' : 'black' }}>Bestått</h4>
                </Oval>
              ))}
            </FlexContainer>
          </FlexContainer>
        </CalculatorCourseContainer>
      )}
    </>
  );
};
export default CalculatorCourse;
